import { useEffect, useMemo, useState, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import SoftTypography from "components/SoftTypography";
import RolesTabs from "./tabs";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { useRoles } from "hooks/useRoles";
import DefaultAdminIcon from "assets/images/DefaultAdminIcon.svg";
import DefaultUserIcon from "assets/images/DefaultUserIcon.svg";
import "./style.css";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import RoleForm from "../RoleForm";
import { DeleteRoleDialog } from "../deleteRoleDialog";
import { deleteRoles } from "api/RolesApi";

const RolesAndPermissionsDetails = () => {
	const { roleId } = useParams();
	const { user } = useAuth0();
	const { users = [] } = useSelector((store) => store.portal.users);
	const navigate = useNavigate();
	const { roles, handleFetchRoles } = useRoles(false);
	const { userPermissions } = useContext(PermissionsContext);
	const [openRoleForm, setOpenRoleForm] = useState(false);
	const [openDeleteRoleDialog, setOpenDeleteRoleDialog] = useState(false);
	const [formMode, setFormMode] = useState("add");

	const role = useMemo(
		() => roles.find((r) => r.id === roleId),
		[roles, roleId]
	);

	const handleOpenRoleForm = (mode) => {
		setFormMode(mode);
		setOpenRoleForm(true);
	};

	const handleCloseRoleForm = () => {
		setOpenRoleForm(false);
	};

	const handleDeleteRole = async () => {
		const result = await deleteRoles(user?.stripeId, roleId);
		if (!result?.isSuccessful) {
			toast.error(result?.message || "Failed to delete role");
			return;
		}
		toast.success("Role deleted successfully");
		setOpenDeleteRoleDialog(false);
		await handleFetchRoles();
		navigate("/administration/roles-and-permissions");
	};

	useEffect(() => {
		if (!roles.length) {
			handleFetchRoles();
		}
	}, [roles, handleFetchRoles]);

	const isAllowedAction = (action) => {
		if (
			userPermissions.some(
				(permission) => permission.name === "owner" && permission.value
			)
		) {
			return true;
		}

		switch (action) {
			case "delete":
				return userPermissions.some(
					(permission) => permission.name === PERMISSIONS.REMOVE_ROLES && permission.value
				);
			case "remove":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.REMOVE_USERS &&
						permission.value
				);
			case "edit":
				return userPermissions.some(
					(permission) =>
						permission.name === PERMISSIONS.EDIT_ROLES && permission.value
				);
			default:
				return false;
		}
	};

	if (!role) {
		return <div>Loading role details...</div>;
	}

	return (
		<>
			{/* Sticky Navbar (same as Flow page) */}
			<div
				style={{
					padding: "8px",
					position: "sticky",
					top: 0,
					zIndex: 1000,
					backgroundColor: "var(--main-bg-color)"
				}}
			>
				<DashboardNavbar title={role.name ?? "Role"} />
			</div>

			<DashboardLayout
				sx={{
					display: "flex",
					flexDirection: "column",
					minHeight: "100vh",
					height: "100vh",
					boxSizing: "border-box",
					overflow: "hidden"
				}}
			>
				<SoftBox
					mt={-3}
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						minHeight: 0
					}}
				>
					{/* Outer transparent container */}
					<Card
						className="role-detail"
						sx={{
							backgroundColor: "transparent !important",
							overflow: "hidden",
							boxShadow: "none !important",
							display: "flex",
							flexDirection: "column",
							flex: 1,
							minHeight: 0
						}}
					>
						{/* Inner main card */}
						<Card
							sx={{
								overflow: "visible",
								display: "flex",
								flexDirection: "column",
								flex: 1,
								minHeight: 0
							}}
						>
							{/* ===== TOP PART (Role Header) ===== */}
							<SoftBox
								alignItems="flex-start"
								justifyContent="space-between"
								sx={{
									display: { sm: "block", lg: "flex" },
									padding: {
										sm: "12px 32px 20px 32px",
										lg: "48px 64px 46px 64px"
									},
									flexWrap: { sm: "wrap", lg: "noWrap" },
									columnGap: "40px",
									height: "max-content"
								}}
							>
								{/* Left: Role info */}
								<SoftBox
									display="flex"
									alignItems="flex-start"
									sx={{
										marginBottom: { sm: "16px", lg: "0" },
										position: "relative"
									}}
								>
									{/* Go Back */}
									<SoftBox
										sx={{
											position: "absolute",
											top: "-32px",
											left: 0
										}}
									>
										<button
											onClick={() =>
												navigate(
													"/administration/roles-and-permissions"
												)
											}
											style={{
												alignItems: "center",
												background: "transparent",
												border: "none",
												cursor: "pointer",
												display: "flex",
												maxWidth: "max-content",
												fontSize: "14px",
												padding: 0
											}}
										>
											<SoftBox
												sx={{
													display: "grid",
													marginRight: "16px",
													placeContent: "center",
													fontSize: "28px",
													fontWeight: "300"
												}}
											>
												<svg
													width="10"
													height="17"
													viewBox="0 0 10 17"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M8.77783 0.5L0.999657 8.27817L8.77783 16.0563"
														stroke="#7B809A"
													/>
												</svg>
											</SoftBox>
											<SoftTypography fontSize="14px">
												Go back
											</SoftTypography>
										</button>
									</SoftBox>

									{/* Role image */}
									<SoftBox
										component="img"
										borderRadius="16px"
										marginRight="40px"
										sx={{
											objectFit: "contain",
											border: role.image ? "0.5px solid #e6e6e6" : "none",
											width: { sm: "120px", lg: "164px" },
											height: { sm: "120px", lg: "164px" },
											flexShrink: 0
										}}
										src={
											role.image ||
											(role.name?.toLowerCase() === "admin"
												? DefaultAdminIcon
												: DefaultUserIcon)
										}
									/>

									{/* Role text */}
									<SoftBox
										sx={{
											maxWidth: { lg: "clamp(100px, 32vw, 540px)" },
											display: "flex",
											flexDirection: "column",
											minHeight: "164px"
										}}
									>
										<SoftTypography
											fontSize="24px"
											fontWeight="bold"
											lineHeight="1.25"
											marginBottom="8px"
											className="white-to-dark"
										>
											{role.name}
										</SoftTypography>

										<SoftTypography
											sx={{ fontSize: "13px", marginBottom: "16px" }}
										>
											{role.description || "No description"}
										</SoftTypography>

										<SoftBox
											display="flex"
											sx={{ marginTop: "auto", gap: "16px" }}
										>
											{(isAllowedAction("edit") || isAllowedAction("delete")) && (
												<SoftButton
													variant="contained"
													color="primary"
													onClick={() =>
														handleOpenRoleForm("edit")
													}
												>
													<Icon sx={{ marginRight: "8px" }}>edit</Icon>
													Properties
												</SoftButton>
											)}
										</SoftBox>
									</SoftBox>
								</SoftBox>

								{/* Right: meta info */}
								<SoftBox sx={{ flexShrink: 0 }}>
									<SoftTypography
										fontSize="12px"
										marginBottom="4px"
									>
										<span
											style={{
												opacity: 0.6,
												width: "76px",
												display: "inline-block"
											}}
										>
											Date Created:
										</span>
										<span style={{ marginLeft: "10px" }}>
											{formatDateAndTime(
												getLocalDateTimeString(
													role.created_date_time
												)
											)}
										</span>
									</SoftTypography>

									<SoftTypography fontSize="12px">
										<span
											style={{
												opacity: 0.6,
												width: "76px",
												display: "inline-block"
											}}
										>
											Last Edited:
										</span>
										<span style={{ marginLeft: "10px" }}>
											{role.updated_date_time &&
												formatDateAndTime(
													getLocalDateTimeString(
														role.updated_date_time
													)
												)}
										</span>
									</SoftTypography>
								</SoftBox>
							</SoftBox>

							{/* ===== TABS (fills remaining height) ===== */}
							<SoftBox
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									minHeight: 0
								}}
							>
								<RolesTabs
									selectedRole={role}
									roleId={roleId}
									userRoles={roles}
								/>
							</SoftBox>
						</Card>
					</Card>
				</SoftBox>

			{/* dialogs */}
			<DeleteRoleDialog
				open={openDeleteRoleDialog}
				onClose={() => setOpenDeleteRoleDialog(false)}
				onAccept={handleDeleteRole}
				isRoleUsed={users.some((item) => item.role_id === roleId)}
			/>
			{openRoleForm && (
					<RoleForm
						open={openRoleForm}
						onClose={handleCloseRoleForm}
						mode={formMode}
						selectedRole={role}
						onRoleUpdated={handleFetchRoles}
						canEdit={isAllowedAction("edit")}
						canDelete={isAllowedAction("delete")}
						onDelete={() => { setOpenRoleForm(false); setOpenDeleteRoleDialog(true); }}
					/>
				)}
			</DashboardLayout>
		</>
	);
};

export default RolesAndPermissionsDetails;
